// src/pages/Consultation/SessionDetail/Modals/OrderSuccessModal.jsx

import React from "react";
import {
  FiCheckCircle,
  FiRefreshCw,
  FiPrinter,
  FiEye,
  FiPlus,
} from "react-icons/fi";
import Button from "../../../../components/common/Button";

const OrderSuccessModal = ({
  isOpen,
  onClose,
  orderData,
  sessionCode,
  onViewOrder,
  onPrintReceipt,
  onStartNewSession,
}) => {
  if (!isOpen) return null;

  const order = orderData?.order;
  const transactionSummary = orderData?.transactionSummary;
  const hasRefund = order?.refund?.required;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />

      {/* Modal */}
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg">
          {/* Success Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <FiCheckCircle className="text-green-600 text-3xl" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Order Created Successfully!
              </h2>
              <p className="text-sm text-gray-600">
                Order ID: <span className="font-medium">{order?.orderId}</span>
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Session: {sessionCode}
              </p>
            </div>
          </div>

          {/* Order Details */}
          <div className="p-6 space-y-4">
            {/* Transaction Summary */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-800 mb-3">
                Transaction Summary
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Type:</span>
                  <span className="font-medium">
                    {transactionSummary?.type}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Order Total:</span>
                  <span className="font-medium">
                    {transactionSummary?.orderTotal}
                  </span>
                </div>
                {transactionSummary?.exchangeValue !== "₹0" && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Exchange Value:</span>
                    <span className="font-medium text-green-600">
                      {transactionSummary?.exchangeValue}
                    </span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t border-gray-200">
                  <span className="text-gray-600 font-semibold">
                    Net Amount:
                  </span>
                  <span className="font-bold text-lg text-primary">
                    {transactionSummary?.netAmount}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 text-xs">Flow:</span>
                  <span className="text-xs text-gray-500">
                    {transactionSummary?.flowDirection}
                  </span>
                </div>
              </div>
            </div>

            {/* Refund Alert */}
            {hasRefund && (
              <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-4">
                <div className="flex gap-3">
                  <FiRefreshCw className="text-orange-600 flex-shrink-0 mt-0.5 text-xl" />
                  <div>
                    <p className="font-semibold text-orange-800">
                      Refund Processing Required
                    </p>
                    <p className="text-sm text-orange-700 mt-1">
                      Refund Amount: ₹{order.refund.amount.toLocaleString()}
                    </p>
                    <p className="text-xs text-orange-600 mt-1">
                      Method: {order.refund.method}
                    </p>
                    {order.refund.requiresApproval && (
                      <p className="text-xs text-orange-600 mt-1 font-medium">
                        ⚠️ Requires manager approval before processing
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Payment Status */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Payment Status:</span>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-medium ${
                    order?.payment?.status === "COMPLETED"
                      ? "bg-green-100 text-green-700"
                      : "bg-yellow-100 text-yellow-700"
                  }`}
                >
                  {order?.payment?.status}
                </span>
              </div>
              {order?.payment?.amountDue > 0 && (
                <p className="text-xs text-gray-600 mt-2">
                  Amount Due: ₹{order.payment.amountDue.toLocaleString()}
                </p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="p-6 border-t border-gray-200 space-y-3">
            <Button onClick={onViewOrder} className="w-full" variant="primary">
              <FiEye className="mr-2" />
              View Order Details
            </Button>

            <div className="grid grid-cols-2 gap-3">
              <Button onClick={onPrintReceipt} variant="outline" size="sm">
                <FiPrinter className="mr-2" />
                Print Receipt
              </Button>
              <Button onClick={onStartNewSession} variant="outline" size="sm">
                <FiPlus className="mr-2" />
                New Session
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccessModal;
